import { useMemo, useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import {
    FaBriefcase,
    FaPlus,
    FaSearch,
} from "react-icons/fa"
import RegisterOrganizationModal from "@/modules/dashboard/ui/components/RegisterOrganizationModal"
import ActivateSubscriptionModal from "@/modules/subscriptions/ui/components/ActivateSubscriptionModal"
import { Button } from "@/modules/shared/ui/components/button"
import {
    DataTable,
    type TableColumn,
} from "@/modules/shared/ui/components/custom/CustomTable"
import { Organization } from "@/modules/organizations/entities/Organization"
import { PlanType } from "@/modules/organizations/types/plan-type"
import { useOrganizationsService } from "@/modules/organizations/hooks/use-organizations"
import { PendingOrganizationDialog } from "../components/PendingOrganizationDialog"
import { InactiveOrganizationDialog } from "../components/InactiveOrganizationDialog"
import { OrganizationStatus } from "@/modules/organizations/enums/organization-status"
import { SearchBar } from "@/modules/shared/ui/components/custom/SearchBar"
import { vacancyService } from "@/modules/recruitment/infrastructure/vacancy-service"
import { dashboardService, DashboardStatsDto } from "@/modules/dashboard/infrastructure/dashboard-service"
import { RecruitmentSection } from "../components/RecruitmentSection"
import { PLAN_STYLES, STATUS_STYLES } from "../components/COLOR_GROUPS"
import { OnboardingSection } from "../components/OnboardingSection"

export default function DashboardPage() {
    const navigate = useNavigate()
    const {
        organizations,
        currentOrganization,
        isLoading: orgsLoading,
        error,
        deleteOrganization,
        selectOrganization,
        loadOrganizations,
    } = useOrganizationsService()

    const [showRegisterModal, setShowRegisterModal] = useState(false)
    const [showActivateModal, setShowActivateModal] = useState(false)
    const [selectedOrgForActivation, setSelectedOrgForActivation] =
        useState<Organization | null>(null)
    const [searchTerm, setSearchTerm] = useState("")
    const [pendingDialogOrg, setPendingDialogOrg] = useState<Organization | null>(null)
    const [inactiveDialogOrg, setInactiveDialogOrg] = useState<Organization | null>(null)
    const [deleteConfirmOrg, setDeleteConfirmOrg] = useState<Organization | null>(null)
    const [vacancyCounts, setVacancyCounts] = useState<Record<string, number>>({})

    const plan = currentOrganization?.plan
    const showRecruitment = plan === PlanType.Recruitment || plan === PlanType.FullHiring
    const showOnboarding = plan === PlanType.Onboarding || plan === PlanType.FullHiring

    const { data: stats, isLoading: statsLoading } = useQuery<DashboardStatsDto>({
        queryKey: ["dashboard-stats", currentOrganization?.id],
        queryFn: () => dashboardService.getStats(currentOrganization!.id),
        enabled: !!currentOrganization?.id,
        staleTime: 60_000,
        retry: false,
        refetchOnWindowFocus: false,
    })

    const visibleOrganizations = useMemo(
        () => organizations.filter((org) => !org.isDeleted),
        [organizations],
    )

    useEffect(() => {
        if (visibleOrganizations.length === 0) return
        Promise.all(
            visibleOrganizations.map((org) =>
                vacancyService
                    .getVacancies(org.id)
                    .then((v) => [org.id, v.length] as const)
                    .catch(() => [org.id, 0] as const),
            ),
        ).then((entries) => setVacancyCounts(Object.fromEntries(entries)))
    }, [visibleOrganizations])

    const filteredOrganizations = useMemo(
        () =>
            visibleOrganizations.filter(
                (org) =>
                    !searchTerm ||
                    org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    org.plan.toLowerCase().includes(searchTerm.toLowerCase()),
            ),
        [visibleOrganizations, searchTerm],
    )
    const handleSelectOrganization = (org: Organization) => {
        const isPending = org.status === OrganizationStatus.Pending || !org.status
        const isInactive = org.status === OrganizationStatus.Inactive
        if (isPending) { setPendingDialogOrg(org); return }
        if (isInactive) { setInactiveDialogOrg(org); return }
        selectOrganization(org)
        navigate("/dashboard")
    }

    const handleActivateSubscription = (org: Organization) => {
        setSelectedOrgForActivation(org)
        setShowActivateModal(true)
    }


    const columns: TableColumn<Organization>[] = [
        {
            key: "name",
            label: "Organisation",
            render: (org) => (
                <Button
                    variant="link"
                    className="p-0 h-auto font-medium text-foreground hover:text-primary cursor-pointer"
                    onClick={() => handleSelectOrganization(org)}
                >
                    {org.name}
                </Button>
            ),
        },
        {
            key: "plan",
            label: "Plan",
            render: (org) => (
                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${PLAN_STYLES[org.plan] ?? "bg-muted text-muted-foreground"}`}>
                    {org.plan}
                </span>
            ),
        },
        {
            key: "status",
            label: "Status",
            render: (org) => {
                const status = org.status || OrganizationStatus.Pending
                return (
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${STATUS_STYLES[status] ?? "bg-muted text-muted-foreground"}`}>
                        {status}
                    </span>
                )
            },
        },
        {
            key: "createdAt",
            label: "Created",
            render: (org) => (
                <span className="text-sm text-muted-foreground">
                    {new Date(org.createdAt).toLocaleDateString("en-GB")}
                </span>
            ),
        },
        {
            key: "updatedAt",
            label: "Vacancies",
            headerClassName: "text-center",
            render: (org) => (
                <div className="text-center">
                    <span className="text-sm font-medium text-foreground">
                        {vacancyCounts[org.id] ?? 0}
                    </span>
                </div>
            ),
        },
        {
            key: "id",
            label: "",
            headerClassName: "text-right",
            render: (org) => {
                const canActivate =
                    org.status === OrganizationStatus.Pending ||
                    org.status === OrganizationStatus.Inactive
                return (
                    <div className="text-right">
                        <Button
                            size="sm"
                            variant="outline"
                            hidden={!canActivate}
                            onClick={() => handleActivateSubscription(org)}
                            className="border-primary text-primary hover:bg-primary/10 dark:hover:bg-primary/20"
                        >
                            Activate Subscription
                        </Button>
                    </div>
                )
            },
        },
    ]

    return (
        <div className="space-y-6">

            {/* ── Page title + primary action ── */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
                    <p className="text-sm text-muted-foreground mt-0.5">
                        Manage your organisations and view analytics
                    </p>
                </div>
                <Button
                    onClick={() => setShowRegisterModal(true)}
                    className="text-primary-foreground"
                >
                    <FaPlus className="mr-2 h-3.5 w-3.5" />
                    New Organisation
                </Button>
            </div>

            {/* ── Analytics (shown only when an org is selected) ── */}
            {!!currentOrganization && (
                <div className="space-y-5">
                    {showRecruitment && (
                        <RecruitmentSection stats={stats} loading={statsLoading} />
                    )}

                    {showRecruitment && showOnboarding && (
                        <hr className="border-border" />
                    )}

                    {showOnboarding && (
                        <OnboardingSection stats={stats} loading={statsLoading} />
                    )}
                </div>
            )}

            {/* ── Organisation table ── */}
            <div className="bg-card rounded-lg border border-border shadow-sm">
                <div className="px-6 py-4 border-b border-border flex items-center justify-between gap-2">
                    <h2 className="text-muted-foreground font-semibold shrink-0">
                        Organisations
                        {!orgsLoading && (
                            <span className="ml-2 text-xs font-normal text-muted-foreground">
                                {filteredOrganizations.length}{" "}
                                {filteredOrganizations.length === 1 ? "organisation" : "organisations"}
                            </span>
                        )}
                    </h2>
                    <SearchBar
                        placeholder="Search organisations…"
                        value={searchTerm}
                        onChange={setSearchTerm}
                        className="w-80 sm:w-64"
                    />
                </div>
                <div className="px-6 py-4">
                    {orgsLoading ? (
                        <div className="py-8 text-center text-sm text-muted-foreground animate-pulse">
                            Loading organisations…
                        </div>
                    ) : error ? (
                        <div className="py-8 text-center text-sm text-destructive">Error: {error}</div>
                    ) : visibleOrganizations.length === 0 ? (
                        <div className="py-16 text-center">
                            <FaBriefcase className="h-10 w-10 mx-auto mb-4 text-muted-foreground" />
                            <p className="text-base font-medium text-foreground-muted mb-1">No Organisations Yet</p>
                            <p className="text-sm text-muted-foreground mb-6">
                                Get started by creating your first organisation.
                            </p>
                            <Button
                                onClick={() => setShowRegisterModal(true)}
                                className="text-primary-foreground"
                            >
                                <FaPlus className="mr-2 h-3.5 w-3.5" />
                                New Organisation
                            </Button>
                        </div>
                    ) : (
                        <DataTable
                            columns={columns}
                            data={filteredOrganizations}
                            emptyMessage={
                                searchTerm ? (
                                    <div className="text-center py-4">
                                        <FaSearch className="mx-auto mb-3 text-muted-foreground/30" />
                                        <p className="text-sm text-muted-foreground">
                                            No organisations matching "{searchTerm}"
                                        </p>
                                    </div>
                                ) : (
                                    "No organisations available"
                                )
                            }
                        />
                    )}
                </div>
            </div>

            {/* ── Modals ── */}
            <RegisterOrganizationModal
                isOpen={showRegisterModal}
                onClose={() => setShowRegisterModal(false)}
            />

            <ActivateSubscriptionModal
                isOpen={showActivateModal}
                onClose={() => {
                    setShowActivateModal(false)
                    setSelectedOrgForActivation(null)
                }}
                organization={selectedOrgForActivation}
                onSubscriptionActivated={loadOrganizations}
            />

            <PendingOrganizationDialog
                organization={pendingDialogOrg}
                onClose={() => setPendingDialogOrg(null)}
            />

            <InactiveOrganizationDialog
                organization={inactiveDialogOrg}
                onClose={() => setInactiveDialogOrg(null)}
            />

            {deleteConfirmOrg && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center z-70">
                    <div className="bg-card rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
                        <h3 className="text-lg font-bold mb-2 text-foreground">Delete Organisation</h3>
                        <p className="text-sm text-muted-foreground mb-3">
                            Are you sure you want to delete this organisation? This action cannot be undone.
                        </p>
                        <p className="text-sm text-amber-600 dark:text-amber-400 mb-5">
                            This will permanently remove the organisation and its subscription.
                        </p>
                        <div className="flex justify-end gap-3">
                            <Button variant="outline" onClick={() => setDeleteConfirmOrg(null)}>
                                Cancel
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={async () => {
                                    await deleteOrganization(deleteConfirmOrg.id)
                                    setDeleteConfirmOrg(null)
                                }}
                            >
                                Delete Organisation
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
